import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';
import { ArrowLeftIcon, MailIcon } from '../../components/icons';

const RESEND_SECONDS = 60;

/**
 * Request a password-reset link.
 *
 * The form used to call nothing at all: submitting set a flag and printed
 * "check your inbox", so a locked-out platform admin followed a credible
 * recovery path and waited for an email that was never sent. It calls
 * `POST /v1/auth/forgot-password` now. That endpoint answers 200 whether or not
 * the account exists, which is why the copy is deliberately conditional and why
 * a failure here can only mean the request itself did not get through.
 */
export function ForgotForm() {
  const t = useT();
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function send() {
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email);
      setSentTo(email);
      setCooldown(RESEND_SECONDS);
    } catch {
      setError(t('auth.forgot.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await send();
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* The field stays on screen after sending: the whole form used to be
            replaced by the confirmation, so an operator who mistyped their
            address had to navigate back to see what they had typed. */}
        <TextInput
          label={t('common.email')}
          description={t('auth.forgot.emailDescription')}
          leftSection={<MailIcon />}
          type="email"
          placeholder={t('auth.login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
          autoFocus
        />

        <div role="alert" aria-live="polite">
          {error && <Alert color="error">{error}</Alert>}
        </div>

        {sentTo && (
          <Alert color="info" title={t('auth.forgot.sentTitle')}>
            <Stack gap="sm" align="flex-start">
              <Text fz="sm">{t('auth.forgot.sent.body', { email: sentTo })}</Text>
              <Group gap="xs" align="baseline">
                <Text fz="sm">{t('auth.forgot.resendPrompt')}</Text>
                {cooldown > 0 ? (
                  <Text fz="sm" c="var(--rr-color-text-muted)">{t('auth.forgot.resendIn', { seconds: cooldown })}</Text>
                ) : (
                  <button type="button" className="admin-auth__inline-link" onClick={() => void send()}>
                    {t('auth.forgot.resend')}
                  </button>
                )}
              </Group>
            </Stack>
          </Alert>
        )}

        {sentTo ? (
          <Button onClick={() => navigate('/auth/login')} fullWidth size="md">
            {t('auth.backToSignIn')}
          </Button>
        ) : (
          <Button type="submit" loading={busy} fullWidth size="md">
            {t('auth.forgot.submit')}
          </Button>
        )}

        {!sentTo && (
          <Link to="/auth/login" className="admin-auth__row-link">
            <ArrowLeftIcon />
            {t('auth.backToSignIn')}
          </Link>
        )}
      </Stack>
    </form>
  );
}
